import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FileCheck, Bell, BarChart3, Sparkles, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ConsentPreferences {
  marketing_emails: boolean;
  newsletter: boolean;
  analytics_cookies: boolean;
  personalization: boolean;
  marketing_emails_updated_at: string | null;
  newsletter_updated_at: string | null;
  analytics_cookies_updated_at: string | null;
  personalization_updated_at: string | null;
}

export function ConsentPreferencesSection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState<ConsentPreferences>({
    marketing_emails: false,
    newsletter: false,
    analytics_cookies: true,
    personalization: true,
    marketing_emails_updated_at: null,
    newsletter_updated_at: null,
    analytics_cookies_updated_at: null,
    personalization_updated_at: null,
  });

  useEffect(() => {
    if (user) {
      fetchConsents();
    }
  }, [user]);

  const fetchConsents = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_consents')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setConsents({
          marketing_emails: data.marketing_emails,
          newsletter: data.newsletter,
          analytics_cookies: data.analytics_cookies,
          personalization: data.personalization,
          marketing_emails_updated_at: data.marketing_emails_updated_at,
          newsletter_updated_at: data.newsletter_updated_at,
          analytics_cookies_updated_at: data.analytics_cookies_updated_at,
          personalization_updated_at: data.personalization_updated_at,
        });
      } else {
        // Create initial consent record if it doesn't exist
        await createInitialConsent();
      }
    } catch (error) {
      console.error('Error fetching consent preferences:', error);
    }
  };

  const createInitialConsent = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_consents')
        .insert({
          user_id: user.id,
          marketing_emails: false,
          newsletter: false,
          analytics_cookies: true,
          personalization: true,
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error creating initial consent record:', error);
    }
  };

  const updateConsent = async (field: keyof Pick<ConsentPreferences, 'marketing_emails' | 'newsletter' | 'analytics_cookies' | 'personalization'>, value: boolean) => {
    if (!user) return;

    setLoading(true);
    const updatedAtField = `${field}_updated_at` as keyof ConsentPreferences;
    const now = new Date().toISOString();

    try {
      const { error } = await supabase
        .from('user_consents')
        .upsert({
          user_id: user.id,
          [field]: value,
          [updatedAtField]: now,
        }, {
          onConflict: 'user_id',
        });

      if (error) throw error;

      setConsents(prev => ({
        ...prev,
        [field]: value,
        [updatedAtField]: now,
      }));

      toast({
        title: "Preferences updated",
        description: `Your ${field.replace('_', ' ')} preference has been saved.`,
      });
    } catch (error) {
      console.error('Error updating consent:', error);
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const consentItems = [
    {
      key: 'newsletter' as const,
      icon: Bell,
      title: 'Weekly Newsletter',
      description: 'Receive our weekly travel tips and destination guides',
      legalBasis: 'Consent',
      updatedAt: consents.newsletter_updated_at,
    },
    {
      key: 'marketing_emails' as const,
      icon: Sparkles,
      title: 'Marketing Communications',
      description: 'Special offers, promotions, and product updates',
      legalBasis: 'Consent',
      updatedAt: consents.marketing_emails_updated_at,
    },
    {
      key: 'analytics_cookies' as const,
      icon: BarChart3,
      title: 'Analytics Cookies',
      description: 'Help us improve the app by tracking anonymous usage data',
      legalBasis: 'Legitimate Interest',
      updatedAt: consents.analytics_cookies_updated_at,
    },
    {
      key: 'personalization' as const,
      icon: Sparkles,
      title: 'Personalized Experience',
      description: 'Customize recommendations based on your travel preferences',
      legalBasis: 'Legitimate Interest',
      updatedAt: consents.personalization_updated_at,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCheck className="h-5 w-5" />
          Privacy & Consent Preferences
        </CardTitle>
        <CardDescription>
          Manage how we use your data under GDPR. You can change these settings at any time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Legal Basis Info */}
        <div className="p-4 rounded-lg bg-muted/50 border">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Your Data Rights</p>
              <p>We process your data based on contract performance (to provide the service), legitimate interests (to improve security), consent (for optional features), and legal obligations. You can exercise your GDPR rights at any time.</p>
            </div>
          </div>
        </div>

        {/* Consent Toggles */}
        <div className="space-y-4">
          {consentItems.map((item, index) => (
            <div key={item.key}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border bg-card/50">
                <div className="flex items-start gap-3 flex-1">
                  <item.icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={item.key} className="font-medium cursor-pointer">
                        {item.title}
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                              {item.legalBasis}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Legal basis for processing: {item.legalBasis}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                    <p className="text-xs text-muted-foreground/70">
                      Last updated: {formatDate(item.updatedAt)}
                    </p>
                  </div>
                </div>
                <Switch
                  id={item.key}
                  checked={consents[item.key]}
                  onCheckedChange={(checked) => updateConsent(item.key, checked)}
                  disabled={loading}
                  className="flex-shrink-0"
                />
              </div>
              {index < consentItems.length - 1 && <Separator className="my-2" />}
            </div>
          ))}
        </div>

        {/* Essential Processing Notice */}
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex items-start gap-3">
            <FileCheck className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Essential Processing</p>
              <p className="text-muted-foreground">
                Some data processing is essential to provide the service (account management, trip planning, payments) and cannot be disabled. This is based on contract performance.
              </p>
            </div>
          </div>
        </div>

        {/* Link to Privacy Policy */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button variant="outline" asChild className="flex-1">
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              View Full Privacy Policy
            </a>
          </Button>
          <Button variant="outline" asChild className="flex-1">
            <a href="mailto:info@odyvoy.com?subject=GDPR%20Data%20Request">
              Submit Data Request
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}